import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Menu, X, ChevronDown, Languages } from 'lucide-react';

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState('ENG');
  const location = useLocation();

  const links = [
    ['/', 'Home'],
    ['/about', 'About'],
    ['/blog', 'Blog'],
    ['/contact', 'Contact'],
  ];

  const languages = [
    { code: 'en', label: 'ENG' },
    { code: 'sw', label: 'SWA' },
    { code: 'fr', label: 'FRA' },
    { code: 'ar', label: 'ARA' },
  ];

  const translatePage = (langCode) => {
    const select = document.querySelector('.goog-te-combo');
    if (select) {
      select.value = langCode;
      select.dispatchEvent(new Event('change'));
    }
    setSelectedLang(languages.find(l => l.code === langCode)?.label || 'ENG');
    setLangOpen(false);
  };

  return (
    <nav
      role="navigation"
      aria-label="Main navigation"
      style={{
        background: '#FFFFFF',
        padding: '0 40px',
        height: '68px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        boxShadow: 'none',
      }}>

      {/* Logo */}
      <Link
        to="/"
        aria-label="Spot Finder home"
        style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        <MapPin size={18} color="#00A878" aria-hidden="true" />
        <span style={{
          fontWeight: '900', fontSize: '1.25rem',
          fontStyle: 'italic', color: '#0D1117',
        }}>
          SpotFinder
        </span>
      </Link>

      {/* Center links — desktop */}
      <div className="d-none d-lg-flex align-items-center gap-4">
        {links.map(([path, label]) => {
          const active = location.pathname === path;
          return (
            <Link key={label} to={path}
              aria-current={active ? 'page' : undefined}
              style={{
                textDecoration: 'none',
                color: active ? '#0D1117' : '#6c757d',
                fontWeight: active ? '700' : '500',
                fontSize: '15px',
                transition: 'color 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#0D1117'}
              onMouseLeave={e => e.currentTarget.style.color = active ? '#0D1117' : '#6c757d'}>
              {label}
            </Link>
          );
        })}
      </div>

      {/* Right side — desktop */}
      <div className="d-none d-lg-flex align-items-center gap-3">

        {/* Language switcher */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setLangOpen(!langOpen)}
            aria-label="Select language"
            aria-expanded={langOpen}
            aria-haspopup="listbox"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px',
              color: '#0D1117', fontWeight: '600', fontSize: '14px',
              padding: '6px 10px', borderRadius: '8px',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            <Languages size={16} color="#6c757d" aria-hidden="true" />
            {selectedLang}
            <ChevronDown size={14} color="#6c757d" aria-hidden="true" style={{
              transform: langOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }} />
          </button>

          {/* Dropdown */}
          {langOpen && (
            <div
              role="listbox"
              aria-label="Language options"
              style={{
                position: 'absolute', top: '42px', right: 0,
                background: '#fff', borderRadius: '10px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                border: '1px solid #f0f0f0',
                overflow: 'hidden', minWidth: '110px',
                zIndex: 100,
              }}>
              {languages.map(lang => (
                <button
                  key={lang.code}
                  role="option"
                  aria-selected={selectedLang === lang.label}
                  onClick={() => translatePage(lang.code)}
                  style={{
                    display: 'block', width: '100%',
                    padding: '10px 16px', border: 'none',
                    background: selectedLang === lang.label ? '#f0faf6' : '#fff',
                    color: selectedLang === lang.label ? '#00A878' : '#0D1117',
                    fontWeight: selectedLang === lang.label ? '700' : '500',
                    fontSize: '14px', textAlign: 'left', cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0faf6'}
                  onMouseLeave={e => e.currentTarget.style.background = selectedLang === lang.label ? '#f0faf6' : '#fff'}>
                  {lang.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        <Link
          to="/contact"
          aria-label="Get started with Spot Finder"
          style={{
            background: '#1B2A4A', color: '#fff',
            borderRadius: '10px', padding: '10px 24px',
            fontWeight: '700', fontSize: '15px',
            textDecoration: 'none',
            transition: 'background 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#00A878'}
          onMouseLeave={e => e.currentTarget.style.background = '#1B2A4A'}>
          Get Started
        </Link>
      </div>

      {/* Mobile hamburger */}
      <button
        className="d-lg-none"
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuOpen}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
        {menuOpen
          ? <X size={24} color="#0D1117" aria-hidden="true" />
          : <Menu size={24} color="#0D1117" aria-hidden="true" />}
      </button>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          role="dialog"
          aria-label="Mobile navigation menu"
          style={{
            position: 'absolute', top: '68px', left: 0, right: 0,
            background: '#fff',
            borderTop: '1px solid #f0f0f0',
            padding: '20px 40px 28px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            zIndex: 999,
          }}>
          {links.map(([path, label]) => (
            <Link key={label} to={path}
              aria-current={location.pathname === path ? 'page' : undefined}
              onClick={() => setMenuOpen(false)}
              style={{
                display: 'block', padding: '12px 0',
                color: '#0D1117', textDecoration: 'none',
                fontWeight: '600', fontSize: '15px',
                borderBottom: '1px solid #f5f5f5',
              }}>
              {label}
            </Link>
          ))}

          {/* Mobile language switcher */}
          <div className="d-flex align-items-center gap-2 flex-wrap mt-3">
            {languages.map(lang => (
              <button key={lang.code}
                onClick={() => { translatePage(lang.code); }}
                aria-label={`Switch to ${lang.label}`}
                style={{
                  background: selectedLang === lang.label ? '#00A878' : '#f5f5f5',
                  color: selectedLang === lang.label ? '#fff' : '#0D1117',
                  border: 'none', borderRadius: '6px',
                  padding: '6px 12px', fontWeight: '600',
                  fontSize: '13px', cursor: 'pointer',
                }}>
                {lang.label}
              </button>
            ))}
          </div>

          <Link to="/contact"
            onClick={() => setMenuOpen(false)}
            style={{
              display: 'inline-block', marginTop: '16px',
              background: '#1B2A4A', color: '#fff',
              borderRadius: '10px', padding: '10px 24px',
              fontWeight: '700', fontSize: '15px',
              textDecoration: 'none',
            }}>
            Get Started
          </Link>
        </div>
      )}

    </nav>
  );
}

export default Navbar;